import React from 'react';
import { TABLE_COLORS } from '../Utils/tableDatasetColors';
import { format } from 'date-fns';
import { Table } from '../Enums/Table.enum';
import { ITableColumn } from '../../../Models/Table/ITableColumn';
import QuestionnaireResultTableItem from '../../../Components/Questionnaire/QuestionnaireResultTableItem';
import moment, { Moment } from 'moment-timezone';
import { IHealthData } from './useHealthData';
import useTranslation from '../../../Hooks/useTranslation';
import { useAppContext } from '../../../Services/AppContext';
import { HealthDataType } from '../Enums/HealthDataType.enum';
import { isString } from 'lodash';
import { isMoment } from 'moment';
import { allowShowZeroInTable } from '../Utils/tableUtils';

export interface Index {
	id: string;
	date: string;
	time: {
		value: string;
		color: string;
		textColor: string;
	};
}

export default function useTable() {
	const { t } = useTranslation();
	const { dateFormatMoment } = useAppContext();

	const DEFAULT_BACKGROUND_COLOR = 'rgb(250,250,252)';
	const DEFAULT_TEXT_COLOR = '#000';

	const getBackgroundColor = (
		datasetType: Table,
		type: string,
		value: string | number,
		colorFromApi?: string,
	) => {
		if (colorFromApi) {
			return colorFromApi;
		}

		if (TABLE_COLORS[datasetType]?.colors) {
			for (const color of TABLE_COLORS[datasetType].colors) {
				if (isString(value)) {
					if (isNumeric(value)) {
						value = parseFloat(value);
					} else {
						value = getStringSubstitute(value, type);
					}
				}
				if (color[type]?.from <= value && color[type]?.to >= value) {
					return color.color;
				}
			}
		}
	};

	const getTextColor = (
		datasetType: Table,
		type: string,
		value: string | number,
		colorFromApi?: string,
	): string | undefined => {
		if (colorFromApi) {
			return colorFromApi;
		}
		if (TABLE_COLORS[datasetType]?.colors) {
			for (const color of TABLE_COLORS[datasetType].colors) {
				if (isString(value)) {
					if (isNumeric(value)) {
						value = parseFloat(value);
					} else {
						value = getStringSubstitute(value, type);
					}
				}
				if (color[type]?.from <= value && color[type]?.to >= value) {
					return color.textColor;
				}
			}
		}
	};

	function isNumeric(str) {
		if (typeof str != 'string') return false; // we only process strings!
		return (
			!isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
			!isNaN(parseFloat(str))
		); // ...and ensure strings of whitespace fail
	}

	const getStringSubstitute = (value: string, datasetKey: string) => {
		if (datasetKey === 'meddiBabyLatamSwelling') {
			if (value === 'No') {
				return 0;
			}
			if (value !== '-') {
				return 1;
			}
		}

		switch (value) {
			case 'Sí':
				return 1;
			case 'No':
				return 0;
			case 'Desde 36,5 °C hasta 36,9 °C':
			case 'Desde 37 °C hasta 37,4 °C':
			case 'Rojizos':
				return 1;
			case 'Desde 37,5 °C hasta 38,0 °C':
			case 'Desde 38,1 °C a 38,4 °C':
			case 'Desde 38,5 °C a 39,0 °C':
			case 'Superior a 39,1 °C':
			case 'Amarillentos / Blanquecinos':
			case 'Marrones':
				return 0;
			case 'Cesárea':
			case 'Vaginal':
				return 2;
		}
		return '';
	};

	const getTimeByView = (
		view: string,
		time: Moment,
		forVertical = false,
		dataType: HealthDataType = HealthDataType.DEFAULT,
	) => {
		time = isMoment(time) ? time : moment(time);
		const formattedTime = time.tz(
			Intl.DateTimeFormat()?.resolvedOptions()?.timeZone,
		);
		let val: string = !forVertical
			? formattedTime.format('HH:mm')
			: formattedTime.format(dateFormatMoment + ' HH:mm');
		switch (view) {
			case 'day':
				if (dataType === HealthDataType.RAW) {
					val = formattedTime.format('HH:mm');
				} else {
					val = `Ø ${formattedTime.format('HH:mm')} - ${formattedTime
						.add(1, 'hour')
						.format('HH:mm')}`;
				}
				break;
			case 'week':
			case 'month':
				val = forVertical
					? 'Ø ' + formattedTime.format(dateFormatMoment)
					: t('component.chart.filter.dayAverage') ?? '';
				if (dataType === HealthDataType.RAW) {
					val = formattedTime.format(dateFormatMoment);
				}
				break;
			case 'year':
				val = `Ø ${formattedTime.format('MMM yyyy')}`;
				break;
		}
		return val;
	};

	const getIndexes = (
		data: Array<Record<string, any>>,
		view: string,
		dataType: HealthDataType,
	) => {
		const rows: Array<Index> = [];

		for (const dataset of data) {
			let date: any = null;
			dataset.data.map((item: { time: Moment; value: string; id: string }) => {
				if (!rows.find((i: { id: string }) => i.id === item.id)) {
					const value = Math.round(parseFloat(item.value));
					let formattedDate = format(
						item.time?.toDate(),
						t('base.dateFormatJsReact') ?? '',
					);
					let useDate = '';
					if (date !== formattedDate) {
						if (value != 0) {
							date = formattedDate;
						}
						useDate = formattedDate;
					}
					rows.push({
						id: item.id,
						date: useDate,
						time: {
							value: getTimeByView(view, item.time, false, dataType),
							color: DEFAULT_BACKGROUND_COLOR,
							textColor: DEFAULT_TEXT_COLOR,
						},
					});
				}
			});
		}
		return rows;
	};

	const getTableData = (
		data: Array<Record<string, any>>,
		tableName: Table,
		view: string,
		dataType: HealthDataType,
	) => {
		const indexes = getIndexes(data, view, dataType);
		return fillTable(indexes, data, tableName);
	};

	const fillTable = (
		indexes: Array<Record<string, any>>,
		fromData: Array<Record<string, any>>,
		tableName: Table,
	) => {
		const datasets: Array<Record<string, any>> = indexes;
		fromData.map((dataset: any) => {
			datasets.map(item => {
				const findItem = dataset.data.find(
					(d: { id: string }) => d.id === item.id,
				);
				if (findItem) {
					let value = Math.round(parseFloat(findItem.value));
					item[dataset.key] = {
						value: value != 0 ? value + ' ' + dataset.unit : '-',
						color:
							value != 0
								? getBackgroundColor(tableName, dataset.key, value) ??
								  DEFAULT_BACKGROUND_COLOR
								: DEFAULT_BACKGROUND_COLOR,
						textColor:
							value != 0
								? getTextColor(tableName, dataset.key, value) ??
								  DEFAULT_TEXT_COLOR
								: DEFAULT_TEXT_COLOR,
						source:
							findItem.questionnaire?.title ?? findItem.source?.title ?? null,
					};
					if (value != 0) {
						item['show'] = true;
					}
				} else {
					item[dataset.key] = {
						value: '-',
						color: DEFAULT_BACKGROUND_COLOR,
						textColor: DEFAULT_TEXT_COLOR,
					};
				}
			});
		});
		return datasets;
	};

	const getVerticalTableData = (
		data: any,
		tableName: Table,
		view: string,
		dataType: HealthDataType,
	) => {
		const columns: ITableColumn[] = getVerticalTableColumns(
			data,
			view,
			dataType,
		);
		const rows: object[] = getVerticalTableRows(columns, data, tableName);
		return { columns, rows };
	};

	const getVerticalTableColumns = (
		data: IHealthData[],
		view: string,
		dataType: HealthDataType,
	) => {
		const columns: ITableColumn[] = [];
		for (const dataset of data) {
			dataset.data.map(item => {
				if (!columns.find((i: ITableColumn) => i.key === item.id)) {
					columns.push({
						key: item.id,
						title: getTimeByView(view, item.time, true, dataType),
						minWidth: item.minWidth ?? '150px',
						maxWidth: item.maxWidth ?? '300px',
						renderItem: item2 => {
							const value = item2[item.id];
							if (typeof value === 'undefined') {
								return <></>;
							}
							return (
								<QuestionnaireResultTableItem
									value={item2[item.id]?.value}
									source={item2[item.id]?.source}
									diff={item2[item.id]?.diff}
									color={item2[item.id]?.color}
									textColor={item2[item.id]?.textColor}
								/>
							);
						},
					});
				}
			});
		}
		return columns.sort((a, b) => (a.key > b.key ? 1 : -1));
	};

	const getVerticalTableRows = (
		columns: ITableColumn[],
		fromData: any,
		tableName: Table,
	) => {
		const rows: object[] = [];
		fromData.map((dataset: any) => {
			let tempItem = {
				id: 1,
				name: dataset.description,
				show: false,
			};
			columns.map((item, index) => {
				const findItem = dataset.data.find(
					(d: { id: string }) => d.id === item.key,
				);
				if (findItem) {
					let value;
					if (!isString(findItem.value)) {
						value = Math.round(parseFloat(findItem.value));
						if (dataset.roundValuesTo) {
							value = parseFloat(findItem.value)
								.toFixed(dataset.roundValuesTo)
								.toString();
							value = value.replace('.0', '');
						}
					} else {
						value = findItem.value;
					}
					if (Array.isArray(findItem.value)) {
						let tempVal = '';
						findItem.value.map((a: any, index: number) => {
							tempVal = tempVal + a;
							if (findItem.value.length - 1 > index) {
								tempVal = tempVal + ', ';
							}
						});
						value = tempVal;
					}
					let displayValue = value;
					if (dataset.unit) {
						displayValue = value + ' ' + dataset.unit;
					}
					tempItem = {
						...tempItem,
						[item.key]: {
							value: isString(value)
								? displayValue
								: value != 0
								? displayValue
								: allowShowZeroInTable(findItem?.questionnaire?.title)
								? 0
								: '-',
							color:
								value != 0 ||
								allowShowZeroInTable(findItem?.questionnaire?.title)
									? getBackgroundColor(
											tableName,
											dataset.key,
											value,
											findItem.backgroundColor,
									  ) ?? DEFAULT_BACKGROUND_COLOR
									: DEFAULT_BACKGROUND_COLOR,
							textColor:
								value != 0 ||
								allowShowZeroInTable(findItem?.questionnaire?.title)
									? getTextColor(
											tableName,
											dataset.key,
											value,
											findItem.textColor,
									  ) ?? DEFAULT_TEXT_COLOR
									: DEFAULT_TEXT_COLOR,
							source:
								findItem.questionnaire?.title ?? findItem.source?.title ?? null,
						},
					};
					if (value != 0) {
						tempItem['show'] = true;
					}
					if (allowShowZeroInTable(findItem?.questionnaire?.title)) {
						tempItem['show'] = true;
					}
				} else {
					tempItem = {
						...tempItem,
						[item.key]: {
							value: dataset?.renderEmpty ? dataset.renderEmpty({ item }) : '-',
							color: DEFAULT_BACKGROUND_COLOR,
							textColor: DEFAULT_TEXT_COLOR,
						},
					};
				}
			});
			rows.push(tempItem);
		});
		return rows;
	};

	return {
		getBackgroundColor,
		getTextColor,
		getTimeByView,
		getTableData,
		getVerticalTableData,
	};
}
